import {
  useCallback,
  useRef,
  useState,
  MouseEvent as ReactMouseEvent,
  RefObject,
} from "react"

import { resolveColumnDrop } from "./resolveColumnDrop"

import type { ColumnReorderFn } from "./useColumnOrder"

const Y_OFFSET_NUDGE = 10

type Drag =
  | { state: "idle" }
  | {
      state: "dragging"
      position: { x: number; y: number }
      dragIndex: number
      dropIndex?: number
    }

export function useColumnDragDrop(
  headerRowRef: RefObject<HTMLTableRowElement | null>,
  keys: string[],
  onReorder?: ColumnReorderFn
) {
  const [drag, setDrag] = useState<Drag>({ state: "idle" })

  const rafRef = useRef<number | undefined>(undefined)
  const dragIndexRef = useRef<number | undefined>(undefined)
  const dropIndexRef = useRef<number | undefined>(undefined)
  const fixedGhostY = useRef<number>(0)

  const computeDropIndex = (
    cells: Element[],
    dragIndex: number,
    x: number
  ): number | undefined => {
    if (!cells.length) return undefined

    const first = cells[0].getBoundingClientRect()
    const last = cells[cells.length - 1].getBoundingClientRect()

    if (x <= first.left) return dragIndex === 0 ? undefined : 0
    if (x >= last.right)
      return dragIndex === cells.length - 1 ? undefined : cells.length

    let idx = dragIndex
    for (let i = 0; i < cells.length; i++) {
      if (i === dragIndex) continue
      const rect = cells[i].getBoundingClientRect()

      // Moving right, insert after
      if (i > dragIndex && x > rect.left) {
        idx = i + 1
      }

      // Moving left, insert before
      if (i < dragIndex && x < rect.right) {
        idx = i
        break
      }
    }

    return idx === dragIndex ? undefined : idx
  }

  const handleMouseDown = useCallback(
    (index: number) => (e: ReactMouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const y = e.clientY - Y_OFFSET_NUDGE
      setDrag({
        state: "dragging",
        dragIndex: index,
        position: { x: e.clientX, y },
      })

      dragIndexRef.current = index
      dropIndexRef.current = undefined
      fixedGhostY.current = y

      const onMove = (me: MouseEvent) => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current)

        const x = me.clientX
        const y = fixedGhostY.current

        rafRef.current = requestAnimationFrame(() => {
          const from = dragIndexRef.current
          const position = { x, y }

          const row = headerRowRef.current
          const cells = row
            ? Array.from(
                row.querySelectorAll<HTMLTableCellElement>(
                  "th[data-column-index]"
                )
              )
            : []

          if (from === undefined || cells.length === 0) {
            return setDrag((d) =>
              d.state === "dragging" ? { ...d, position } : d
            )
          }

          const nextDrop = computeDropIndex(cells, from, position.x)
          dropIndexRef.current = nextDrop

          setDrag((d) =>
            d.state === "dragging"
              ? { ...d, dragIndex: from, dropIndex: nextDrop, position }
              : d
          )
        })
      }

      const onUp = () => {
        setDrag({ state: "idle" })

        if (rafRef.current) cancelAnimationFrame(rafRef.current)

        // Translate to semantic intent
        if (
          onReorder &&
          dragIndexRef.current !== undefined &&
          dropIndexRef.current !== undefined
        ) {
          const res = resolveColumnDrop({
            keys,
            from: dragIndexRef.current,
            slot: dropIndexRef.current,
          })
          if (res) {
            onReorder(res)
          }
        }

        dragIndexRef.current = undefined
        dropIndexRef.current = undefined

        document.removeEventListener("mousemove", onMove)
        document.removeEventListener("mouseup", onUp)
      }

      document.addEventListener("mousemove", onMove)
      document.addEventListener("mouseup", onUp)
    },
    [headerRowRef, keys, onReorder]
  )

  const dragging = drag.state === "dragging" ? drag : undefined

  return {
    dragIndex: dragging?.dragIndex,
    dropIndex: dragging?.dropIndex,
    position: dragging?.position,
    state: dragging?.state,
    handleMouseDown,
  }
}
